import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import {
  type BoardTarget,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  TargetSelect,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';
import {
  type StudentAdoptionInfo,
  useStudentAdoptionInfo,
} from './useStudentAdoptionInfo';

interface AdoptStudentModalProps {
  /** The student to adopt. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the adoption has landed, so the board can refresh behind it. */
  onAdopted?: () => void;
}

/**
 * Legacy's two-step adopt popup: the confirm body (or an error instead of it),
 * then the success body.
 *
 * The confirm step is an input, not just a confirmation: the web target chosen
 * there, or the matched campaign, becomes the adoption's attribution.
 */
export function AdoptStudentModal({
  studentId,
  onClose,
  onAdopted,
}: AdoptStudentModalProps) {
  const { t } = useTranslation();
  // The payload snapshot the success body reads, held because the student is
  // off the board once this is set and `/adoption-info` would now answer 409 or
  // 404.
  const [adoptedInfo, setAdoptedInfo] = useState<StudentAdoptionInfo | null>(
    null,
  );
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing now, readable from a settled request.
  // `POST /adopt` can outlast the modal, and without this its continuation
  // would write the first student's success body into the next student's modal.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight: `isPending` disables the button a render late,
  // so a double click would fire two adoptions and the second would get a 409.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body or target choice. Adjusting state during render (rather than in an
  // effect) keeps the first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setAdoptedInfo(null);
    setSelectedTargetId(null);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  const { info, isLoading, error } = useStudentAdoptionInfo(
    studentId,
    adoptedInfo === null,
  );
  const { mutateAsync: adoptStudent, isPending } =
    api.adoptStudent.useMutation();

  const failure = submitError ?? error;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  // Success first: the adoption has landed, so nothing that follows can undo it.
  if (adoptedInfo !== null) {
    return (
      <StudentBoardModal
        title={t('lessons.student-adopted')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.s-is-now-your-student', { name: adoptedInfo.display_name })}
        </ModalParagraph>
        {/* Shown only when the student has completed the spiritual journey
           questions. The link to the student's profile sits outside the
           sentence because the PT_BR translation has no markup to anchor
           it on. */}
        {adoptedInfo.spiritual_journey_completed && (
          <>
            <ModalParagraph>
              {t('wbs.s-has-completed-the-spiritual-journey-questions', {
                name: adoptedInfo.display_name,
              })}
            </ModalParagraph>
            <Link
              to="/members/$memberId/"
              params={{ memberId: String(adoptedInfo.student_id) }}
            >
              {t('wbs.view-students-profile')}
            </Link>
          </>
        )}
      </StudentBoardModal>
    );
  }

  // A typed 4xx from `/adoption-info` arrives instead of the payload, so the
  // modal opens straight into the error, as in legacy. `POST /adopt`'s refusals
  // land in the same place.
  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.adoption-confirmation')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.adoption-confirmation')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  // The picker offers the original targets, not the available ones: legacy
  // keeps passed-on targets and marks them. A student can have more than one.
  const targets = info.original_targets;
  // Re-resolved against the CURRENT list rather than trusted: the payload
  // refetches on window focus, and a target that has since been passed on by
  // another leader must not still be the one posted.
  const postedTargetId =
    targets.find((target) => target.target_id === selectedTargetId)
      ?.target_id ??
    targets[0]?.target_id ??
    null;

  const handleAdopt = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      await adoptStudent({
        body: {
          user_id: studentId,
          // Exactly one attribution, or neither. A matched campaign hides the
          // target picker (see `StudentInfoBlock`) and posting both is
          // `AdoptionTargetConflictError`; posting neither is accepted.
          campaign_id: info.matched_campaign?.campaign_id,
          web_ad_id:
            info.matched_campaign === null && postedTargetId !== null
              ? postedTargetId
              : undefined,
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onAdopted?.();
    if (openSession.current === submittedFor) setAdoptedInfo(info);
  };

  return (
    <StudentBoardModal
      title={t('wbs.adoption-confirmation')}
      onClose={onClose}
      footer={
        <WBSButton
          onClick={() => {
            void handleAdopt();
          }}
          isLoading={isPending}
        >
          {t('wbs.adopt-this-student')}
        </WBSButton>
      }
    >
      <ModalParagraph>
        {t('wbs.are-you-sure-you-want-to-adopt-this-student')}
      </ModalParagraph>
      {/* Legacy's popup adds this only for a postal student, whose adopting
         helper has to post the courses themselves. */}
      {info.profile_type === 'POSTAL_STUDENT' && (
        <ModalParagraph>{t('wbs.adopt-postal-reminder')}</ModalParagraph>
      )}
      <StudentInfoBlock
        info={info}
        targets={targets}
        renderTargets={(options: readonly BoardTarget[]) => (
          <TargetSelect
            aria-label={t('wbs.matched-web-ad-targets')}
            value={String(postedTargetId ?? '')}
            onChange={(event) =>
              setSelectedTargetId(Number(event.target.value))
            }
          >
            {options.map((target) => (
              <option key={target.target_id} value={String(target.target_id)}>
                {target.has_passed_on
                  ? `${targetLabel(target)} ${t('wbs.passed-on-suffix')}`
                  : targetLabel(target)}
              </option>
            ))}
          </TargetSelect>
        )}
      />
    </StudentBoardModal>
  );
}
